<template>
  <div>
    <p class="text-muted">
      Every document this onboarding expects from the client, grouped by who has to provide it.
      Files the client uploads arrive as <strong>Submitted</strong> and wait here for review.
    </p>

    <b-alert v-if="!requirements.length" show variant="info">
      <feather-icon icon="InfoIcon" class="mr-50" />
      No documents are required yet. Add a package on the Packages tab or request one by hand below.
    </b-alert>

    <div v-for="group in groups" :key="group.key" class="mb-2">
      <h6 class="mb-50">
        <feather-icon :icon="group.key === 'account' ? 'BriefcaseIcon' : 'UserIcon'" size="14" class="mr-25" />
        {{ group.title }}
        <small v-if="group.subtitle" class="text-muted ml-25">{{ group.subtitle }}</small>
        <b-badge variant="light-secondary" class="ml-25">{{ group.rows.length }}</b-badge>
      </h6>
      <b-table :items="group.rows" :fields="fields" responsive small class="mb-0">
        <template #cell(Name)="{ item }">
          {{ item.Name }}
          <b-badge v-if="item.Source === 'Manual'" variant="light-info" class="ml-25">Manual</b-badge>
          <b-badge v-if="!item.Required" variant="light-secondary" class="ml-25">Optional</b-badge>
          <small v-if="item.Note" class="text-muted d-block">{{ item.Note }}</small>
          <small v-if="item.PackageName" class="d-block" style="font-size:0.75rem">
            <feather-icon icon="PackageIcon" size="11" class="mr-25" />{{ item.PackageName }}
          </small>
        </template>

        <template #cell(Status)="{ item }">
          <b-badge :variant="requirementStatusVariant(item.Status)">{{ requirementStatusLabel(item.Status) }}</b-badge>
          <small v-if="item.Status === 'Rejected' && item.RejectReason" class="text-danger d-block">
            {{ item.RejectReason }}
          </small>
          <small v-if="item.ReviewedBy && item.Status !== 'Requested'" class="text-muted d-block">
            {{ item.ReviewedByName || "Reviewed" }} · {{ formatDateTime(item.ReviewedAt) }}
          </small>
        </template>

        <template #cell(CurrentDocument)="{ item }">
          <template v-if="item.CurrentDocument">
            <b-link @click="downloadDocument(item.CurrentDocument.id, item.CurrentDocument.FileName)">
              <feather-icon icon="DownloadIcon" size="12" class="mr-25" />{{ item.CurrentDocument.FileName }}
            </b-link>
            <small class="text-muted d-block">
              {{ formatSize(item.CurrentDocument.FileSize) }} · {{ formatDateTime(item.CurrentDocument.created_at) }}
            </small>
            <small v-if="item.CurrentDocument.Note" class="d-block font-italic">
              "{{ item.CurrentDocument.Note }}"
            </small>
          </template>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="{ item }">
          <div class="text-nowrap">
            <template v-if="canReview(item)">
              <b-button
                variant="flat-success"
                size="sm"
                title="Approve"
                :disabled="busyId === item.id"
                @click="approve(item)"
              >
                <feather-icon icon="CheckIcon" size="14" />
              </b-button>
              <b-button
                variant="flat-danger"
                size="sm"
                title="Reject"
                :disabled="busyId === item.id"
                @click="openReject(item)"
              >
                <feather-icon icon="XIcon" size="14" />
              </b-button>
              <b-button
                v-if="item.Status === 'Submitted'"
                variant="flat-secondary"
                size="sm"
                title="Put on hold (under review)"
                :disabled="busyId === item.id"
                @click="hold(item)"
              >
                <feather-icon icon="PauseIcon" size="14" />
              </b-button>
            </template>
            <b-button
              v-if="item.Status !== 'Approved'"
              variant="flat-primary"
              size="sm"
              title="Upload on behalf of the client"
              :disabled="busyId === item.id"
              @click="openUpload(item)"
            >
              <feather-icon icon="UploadIcon" size="14" />
            </b-button>
            <b-button
              v-if="item.Source === 'Manual'"
              variant="flat-danger"
              size="sm"
              title="Remove this request"
              :disabled="busyId === item.id"
              @click="removeRequirement(item)"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
            <b-spinner v-if="busyId === item.id" small class="ml-25" />
          </div>
        </template>
      </b-table>
    </div>

    <!-- Request by hand -->
    <h5 class="mt-1">Request a document by hand</h5>
    <p class="text-muted">
      Ask this account for a specific file. Pick a document type from the catalogue or type
      any name — it is added to this onboarding only, not to the settings.
    </p>
    <b-row class="align-items-end">
      <b-col cols="12" md="4">
        <b-form-group label="Document type" class="mb-1">
          <v-select
            v-model="manual.type"
            :options="documentTypes"
            label="Name"
            placeholder="From the catalogue…"
            :clearable="true"
            :disabled="!!manual.name.trim()"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="4">
        <b-form-group label="Or a one-off name" class="mb-1">
          <b-form-input
            v-model="manual.name"
            placeholder="e.g. Certified copy of passport"
            :disabled="!!manual.type"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="4">
        <b-form-group label="Note (optional)" class="mb-1">
          <b-form-input v-model="manual.note" placeholder="Shown to the client" />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label="Required from" class="mb-1">
          <b-form-select v-model="manual.scope" :options="scopeOptions" @change="manual.person = null" />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="4">
        <b-form-group label="Person" class="mb-1">
          <v-select
            v-model="manual.person"
            :options="scopedPersons"
            label="Label"
            :placeholder="manual.scope === 'Account' ? 'Not needed for account documents' : 'Choose the person…'"
            :disabled="manual.scope === 'Account'"
            :clearable="true"
          />
          <small v-if="manual.scope !== 'Account' && !scopedPersons.length" class="text-warning">
            The client has not entered any {{ scopeLabel(manual.scope).toLowerCase() }} on the form yet.
          </small>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="2">
        <b-form-checkbox v-model="manual.notify" class="mb-1">Notify client</b-form-checkbox>
      </b-col>
      <b-col cols="12" md="3">
        <b-button variant="primary" block class="mb-1" :disabled="!canAddManual || manualSaving" @click="addManual">
          <b-spinner v-if="manualSaving" small class="mr-25" />
          <feather-icon v-else icon="FilePlusIcon" size="14" class="mr-25" />
          Request document
        </b-button>
      </b-col>
    </b-row>

    <review-modal
      v-model="rejectModal"
      title="Reject Document"
      label="Reason (shown to the client)"
      :description="rejectTarget ? `Reject the file uploaded for '${rejectTarget.Name}'? The client will be asked to upload a new one.` : ''"
      placeholder="e.g. The copy is not certified / the document has expired"
      ok-title="Reject"
      ok-variant="danger"
      :busy="!!rejectTarget && busyId === rejectTarget.id"
      @confirm="reject"
    />

    <upload-modal
      v-model="uploadModal"
      title="Upload on behalf of the client"
      :description="uploadTarget ? `The file is attached to '${uploadTarget.Name}'${uploadTarget.PersonLabel ? ' for ' + uploadTarget.PersonLabel : ''} and marked as uploaded by SFC.` : ''"
      :busy="uploading"
      @upload="upload"
    />
  </div>
</template>

<script>
import vSelect from "vue-select";
import onboardingMixin from "./onboardingMixin";
import ReviewModal from "./ReviewModal.vue";
import UploadModal from "./UploadModal.vue";

export default {
  name: "OnboardingRequirementsTab",

  components: { vSelect, ReviewModal, UploadModal },

  mixins: [onboardingMixin],

  props: {
    onboardingId: { type: Number, required: true },
    requirements: { type: Array, default: () => [] },
    persons: { type: Array, default: () => [] },
    personTypes: { type: Object, default: () => ({}) },
    documentTypes: { type: Array, default: () => [] },
  },

  data() {
    return {
      busyId: null,
      rejectModal: false,
      rejectTarget: null,
      uploadModal: false,
      uploadTarget: null,
      uploading: false,
      manualSaving: false,
      manual: {
        type: null,
        name: "",
        note: "",
        scope: "Account",
        person: null,
        notify: true,
      },
      fields: [
        { key: "Name", label: "Document" },
        { key: "Status", label: "Status" },
        { key: "CurrentDocument", label: "Current file" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },

  computed: {
    groups() {
      const groups = [];
      const accountRows = this.requirements.filter((r) => r.Scope === "Account" || !r.PersonId);
      if (accountRows.length) {
        groups.push({ key: "account", title: "Account", subtitle: "", rows: accountRows });
      }

      const seen = new Set();
      this.persons.forEach((person) => {
        const rows = this.requirements.filter((r) => r.Scope !== "Account" && r.PersonId === person.id);
        if (!rows.length) return;
        seen.add(person.id);
        groups.push({
          key: `person-${person.id}`,
          title: person.Label || person.Name || rows[0].PersonLabel || `Person ${person.id}`,
          subtitle: this.scopeLabel(person.PersonType),
          rows,
        });
      });

      // Rows whose person is no longer on the form (should not happen, but never hide them).
      const orphans = this.requirements.filter(
        (r) => r.Scope !== "Account" && r.PersonId && !seen.has(r.PersonId)
      );
      const byPerson = {};
      orphans.forEach((r) => {
        if (!byPerson[r.PersonId]) byPerson[r.PersonId] = [];
        byPerson[r.PersonId].push(r);
      });
      Object.keys(byPerson).forEach((personId) => {
        const rows = byPerson[personId];
        groups.push({
          key: `orphan-${personId}`,
          title: rows[0].PersonLabel || rows[0].PersonName || `Person ${personId}`,
          subtitle: `${this.scopeLabel(rows[0].Scope)} (no longer on the form)`,
          rows,
        });
      });

      return groups;
    },

    scopeOptions() {
      const options = [{ value: "Account", text: "Account" }];
      Object.keys(this.personTypes || {}).forEach((type) => {
        options.push({ value: type, text: this.personTypes[type].label || type });
      });
      return options;
    },

    scopedPersons() {
      if (this.manual.scope === "Account") return [];
      return this.persons.filter((p) => p.PersonType === this.manual.scope);
    },

    canAddManual() {
      const hasName = !!this.manual.type || !!this.manual.name.trim();
      const hasPerson = this.manual.scope === "Account" || !!this.manual.person;
      return hasName && hasPerson;
    },
  },

  methods: {
    scopeLabel(type) {
      if (type === "Account") return "Account";
      const info = this.personTypes && this.personTypes[type];
      return info ? info.label : type || "";
    },

    canReview(item) {
      return !!item.CurrentDocument && (item.Status === "Submitted" || item.Status === "UnderReview");
    },

    review(item, action, reason) {
      this.busyId = item.id;
      return this.dispatchOnboarding("reviewDocument", {
        documentId: item.CurrentDocument.id,
        action,
        reason: reason || null,
      })
        .then((res) => {
          this.toastSuccess((res.data && res.data.message) || "Document reviewed.", "Document Reviewed");
          this.rejectModal = false;
          this.$emit("refresh");
        })
        .catch((err) => this.toastError(err, "Failed to review the document."))
        .finally(() => {
          this.busyId = null;
        });
    },

    approve(item) {
      this.confirmBox(`Approve "${item.CurrentDocument.FileName}" for "${item.Name}"?`, {
        title: "Approve Document",
        okVariant: "success",
        okTitle: "Approve",
      }).then((confirmed) => {
        if (confirmed) this.review(item, "approve");
      });
    },

    openReject(item) {
      this.rejectTarget = item;
      this.rejectModal = true;
    },

    reject(reason) {
      if (!this.rejectTarget) return;
      this.review(this.rejectTarget, "reject", reason);
    },

    hold(item) {
      this.review(item, "hold");
    },

    removeRequirement(item) {
      this.confirmBox(`Remove the request for "${item.Name}"? The client will no longer be asked for it.`, {
        title: "Remove Request",
        okVariant: "danger",
        okTitle: "Remove",
      }).then((confirmed) => {
        if (!confirmed) return;
        this.busyId = item.id;
        this.dispatchOnboarding("removeRequiredDocument", { requirementId: item.id })
          .then(() => this.$emit("refresh"))
          .catch((err) => this.toastError(err, "Failed to remove the request."))
          .finally(() => {
            this.busyId = null;
          });
      });
    },

    openUpload(item) {
      this.uploadTarget = item;
      this.uploadModal = true;
    },

    upload({ file, note }) {
      if (!this.uploadTarget) return;
      this.uploading = true;
      const formData = new FormData();
      formData.append("onboardingId", this.onboardingId);
      formData.append("file", file);
      formData.append("requirementId", this.uploadTarget.id);
      if (note) formData.append("note", note);

      this.dispatchOnboarding("uploadInternalDocument", formData)
        .then((res) => {
          this.toastSuccess((res.data && res.data.message) || "Document uploaded.", "Uploaded");
          this.uploadModal = false;
          this.$emit("refresh");
        })
        .catch((err) => this.toastError(err, "Upload failed."))
        .finally(() => {
          this.uploading = false;
        });
    },

    addManual() {
      if (!this.canAddManual || this.manualSaving) return;
      this.manualSaving = true;
      this.dispatchOnboarding("addRequiredDocument", {
        onboardingId: this.onboardingId,
        documentTypeId: this.manual.type ? this.manual.type.id : null,
        name: this.manual.type ? null : this.manual.name.trim(),
        note: this.manual.note.trim() || null,
        scope: this.manual.scope,
        personId: this.manual.scope === "Account" ? null : this.manual.person.id,
        notify: this.manual.notify,
      })
        .then((res) => {
          this.toastSuccess((res.data && res.data.message) || "Document requested.", "Document Requested");
          this.manual.type = null;
          this.manual.name = "";
          this.manual.note = "";
          this.manual.person = null;
          this.$emit("refresh");
        })
        .catch((err) => this.toastError(err, "Failed to request the document."))
        .finally(() => {
          this.manualSaving = false;
        });
    },
  },
};
</script>
